import React, { useState } from "react";
import type { GhlSendDraft } from "../types/ghlBookingChat";
import { sendApprovedGhlMessage } from "../../../../features/ghl/services/ghlSendMessage";

interface SendDraftCardProps {
  draft: GhlSendDraft;
  customerId: string;
  /** Optional: authedFetch resolves a fresh token itself, so this is only a
   *  seed for the freshness check, not the source of auth. */
  idToken?: string;
}

type Phase = "review" | "sending" | "sent" | "cancelled" | "error";

/**
 * SGL-244: the approval gate's UI half.
 *
 * `ghl_send_message` cannot send — it returns this draft, and the send happens
 * only when the owner picks "Send it" here. Deliberately a dropdown plus one
 * button rather than an inline editor: the point is a decision the model
 * cannot make for itself, and every extra control is more surface between the
 * owner and that decision. Editing stays where it already works — the owner
 * asks the assistant to reword it, which produces a new draft.
 */
export const SendDraftCard: React.FC<SendDraftCardProps> = ({ draft, customerId, idToken }) => {
  const [choice, setChoice] = useState<"send" | "cancel">("send");
  const [phase, setPhase] = useState<Phase>("review");
  const [error, setError] = useState<string>("");

  const isEmail = draft.type === "EMAIL";
  const recipient = draft.contact_name || draft.contact_handle || "this contact";
  const locked = phase === "sending" || phase === "sent" || phase === "cancelled";

  const onConfirm = async () => {
    if (choice === "cancel") {
      setPhase("cancelled");
      return;
    }
    setPhase("sending");
    setError("");
    try {
      await sendApprovedGhlMessage({
        customerId,
        contactId: draft.contact_id,
        type: draft.type,
        message: draft.message,
        subject: draft.subject,
        idToken,
      });
      setPhase("sent");
    } catch (e) {
      // Never show "sent" on a failure — the whole card exists so the owner
      // knows what actually left their domain.
      setError(e instanceof Error ? e.message : "Couldn't send. Try again.");
      setPhase("error");
    }
  };

  return (
    <div className="my-1 w-full max-w-[420px] overflow-hidden rounded-2xl border border-theme-border bg-theme-surface">
      <div className="flex items-center justify-between gap-2 border-b border-theme-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-theme-background">
            <span className="material-icons-outlined text-[18px] text-theme-fg-muted">
              {isEmail ? "mail_outline" : "sms"}
            </span>
          </span>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-bold text-theme-fg">
              {phase === "sent" ? "Sent" : `Draft ${isEmail ? "email" : "SMS"}`}
            </p>
            <p className="truncate text-[11.5px] text-theme-fg-muted">
              To {recipient}
              {draft.contact_name && draft.contact_handle ? ` · ${draft.contact_handle}` : ""}
            </p>
          </div>
        </div>
        {phase === "sent" && (
          <span className="material-icons-outlined flex-shrink-0 text-[18px] text-theme-fg-muted">
            check_circle
          </span>
        )}
      </div>

      <div className="px-4 py-3">
        {isEmail && draft.subject && (
          <p className="mb-1.5 text-[12.5px] font-semibold text-theme-fg">{draft.subject}</p>
        )}
        <p className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-theme-fg-muted">
          {draft.message}
        </p>
      </div>

      {phase === "review" && (
        <div className="flex items-center gap-2 border-t border-theme-border px-4 py-3">
          <select
            aria-label="What to do with this draft"
            value={choice}
            onChange={(e) => setChoice(e.target.value as "send" | "cancel")}
            className="flex-1 rounded-lg border border-theme-border bg-theme-background px-2.5 py-1.5 text-[12.5px] text-theme-fg"
          >
            <option value="send">Send it</option>
            <option value="cancel">Don't send</option>
          </select>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-lg bg-theme-accent px-3.5 py-1.5 text-[12.5px] font-semibold text-white disabled:opacity-60"
          >
            Confirm
          </button>
        </div>
      )}

      {locked && (
        <div className="border-t border-theme-border px-4 py-2.5">
          <p className="text-[11.5px] text-theme-fg-muted">
            {phase === "sending" && "Sending…"}
            {phase === "sent" && `Sent to ${recipient}.`}
            {phase === "cancelled" && "Not sent. Ask for a change and I'll redraft it."}
          </p>
        </div>
      )}

      {phase === "error" && (
        <div className="border-t border-theme-border px-4 py-2.5">
          <p className="text-[11.5px] text-theme-fg-muted">{error}</p>
          <button
            type="button"
            onClick={() => setPhase("review")}
            className="mt-1 text-[11.5px] font-semibold text-theme-accent"
          >
            Try again
          </button>
        </div>
      )}
    </div>
  );
};
